import React from 'react';
import { Table } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { verifyValue } from '../../../specialCore';
import { calculateKroKrwIntersection } from './krIntersection';
import { CorrelationEndpointSource } from '../../../../../@types/projects/types';
import { resolveWaterOilValues } from './valueResolver';

const columns = [
  {
    title: (
      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
        Relative permeability endpoints
      </div>
    ),
    dataIndex: 'combined',
    key: 'combined',
    render: (text: string) => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 8px'
        }}
      >
        <span>{text.split(' - ')[0]}</span>
        <span>{text.split(' - ')[1]}</span>
      </div>
    )
  }
];

const format4 = (v: number | string | null | undefined) => {
  if (typeof v === 'string') {
    return v;
  }
  return v == null || Number.isNaN(v) ? verifyValue(v as any) : v.toFixed(4);
};

const intersectionColumns = [
  {
    title: 'Sw',
    dataIndex: 'sw',
    key: 'sw',
    align: 'center' as const,
    render: (value: number | string | null) => format4(value)
  },
  {
    title: 'Kro=Krw',
    dataIndex: 'kroKrw',
    key: 'kroKrw',
    align: 'center' as const,
    render: (value: number | string | null) => format4(value)
  }
];

interface CoreyExponentsTableProps {
  endpointSource: CorrelationEndpointSource;
}

const CoreyExponentsTable: React.FC<CoreyExponentsTableProps> = ({
  endpointSource
}) => {
  const { selectedInfo } = useSelected();
  const sampleCurves = selectedInfo?.sample?.input_sample_curves?.water_oil;
  const swValues = sampleCurves?.sw_krel ?? [];
  const resolved = resolveWaterOilValues(selectedInfo?.sample, endpointSource);

  const intersection = calculateKroKrwIntersection({
    swValues,
    swi: resolved.swi ?? swValues?.[0],
    sor: resolved.sor,
    no: resolved.no,
    nw: resolved.nw,
    krwEndPoint: resolved.krwEndpoint
  });

  const endpointLabelSuffix =
    endpointSource === 'refinement' ? 'refinement' : 'absolute';

  const data = [
    {
      key: '1',
      combined: `Swi - ${verifyValue(selectedInfo?.sample?.input_sample_curves?.water_oil?.sw_krel?.[0])}`
    },
    {
      key: '2',
      combined: `No - ${format4(resolved.no)}`
    },
    {
      key: '3',
      combined: `Nw - ${format4(resolved.nw)}`
    },
    {
      key: '4',
      combined: `Krw ${endpointLabelSuffix} - ${format4(resolved.krwEndpoint)}`
    },
    {
      key: '5',
      combined: `Kro ${endpointLabelSuffix} - ${format4(resolved.kroEndpoint)}`
    },
    {
      key: '6',
      combined: `Sor${endpointSource === 'refinement' ? ' refinement' : ''} - ${format4(resolved.sor)}`
    }
  ];

  const intersectionDisplay =
    intersection.status === 'valid' ? null : 'Not available';
  const intersectionData = [
    {
      key: '1',
      sw:
        intersection.status === 'valid'
          ? intersection.intersectionSw
          : intersectionDisplay,
      kroKrw:
        intersection.status === 'valid'
          ? intersection.intersectionK
          : intersectionDisplay
    }
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        size="small"
        style={{ padding: '0 8px' }}
      />
      <Table
        columns={intersectionColumns}
        dataSource={intersectionData}
        pagination={false}
        size="small"
        style={{ padding: '0 8px', marginTop: '0.5rem' }}
        title={() => (
          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Finding Sw @ Kro=Krw
          </div>
        )}
      />
    </div>
  );
};

export default CoreyExponentsTable;
